import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Slider } from '../../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Search, Filter, MapPin, Star, Clock, Shield, 
  GraduationCap, Globe, Users, ChevronDown, X,
  SlidersHorizontal, BookOpen, Award
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Regions for location filtering
const UK_REGIONS = [
  { value: 'london', label: 'London' },
  { value: 'south_east', label: 'South East' },
  { value: 'south_west', label: 'South West' },
  { value: 'midlands', label: 'Midlands' },
  { value: 'north_west', label: 'North West' },
  { value: 'north_east', label: 'North East' },
  { value: 'yorkshire', label: 'Yorkshire' },
  { value: 'scotland', label: 'Scotland' },
  { value: 'wales', label: 'Wales' },
];

const LK_REGIONS = [
  { value: 'western', label: 'Western Province' },
  { value: 'central', label: 'Central Province' },
  { value: 'southern', label: 'Southern Province' },
  { value: 'northern', label: 'Northern Province' },
  { value: 'eastern', label: 'Eastern Province' },
  { value: 'north_western', label: 'North Western' },
  { value: 'north_central', label: 'North Central' },
  { value: 'uva', label: 'Uva Province' },
  { value: 'sabaragamuwa', label: 'Sabaragamuwa' },
];

// Tutor Card Component
function TutorCard({ tutor, onClick }) {
  const currencySymbol = tutor.currency === 'LKR' || (tutor.country || '').toUpperCase() === 'LK' || (tutor.country || '').toUpperCase() === 'SL' ? 'Rs.' : '£';
  
  return (
    <Card 
      className="hover:shadow-lg transition-all cursor-pointer border-l-4 border-l-indigo-500 group"
      onClick={onClick}
      data-testid={`tutor-card-${tutor.id}`}
    >
      <CardContent className="p-4">
        <div className="flex gap-4">
          {/* Profile Image */}
          <div className="relative">
            <img 
              src={tutor.profile_image || `https://ui-avatars.com/api/?name=${encodeURIComponent(tutor.name)}&background=6366f1&color=fff&size=96`}
              alt={tutor.name}
              className="w-20 h-20 rounded-full object-cover border-2 border-indigo-100 group-hover:border-indigo-300 transition-all"
            />
            {tutor.has_profile_photo && (
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                <Shield className="w-3 h-3 text-white" />
              </div>
            )}
          </div>
          
          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-lg text-gray-900 group-hover:text-indigo-600 transition-colors">
                  {tutor.name}
                </h3>
                <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                  <MapPin className="w-3.5 h-3.5" />
                  <span>{tutor.location || tutor.country}</span>
                </div>
              </div>
              
              {/* Rating */}
              <div className="text-right">
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                  <span className="font-semibold">{(tutor.rating || 0).toFixed(1)}</span>
                </div>
                <p className="text-xs text-gray-500">{tutor.total_reviews || 0} reviews</p>
              </div>
            </div>
            
            {/* Subjects */}
            <div className="flex flex-wrap gap-1 mt-2">
              {(tutor.subjects || []).slice(0, 4).map(subject => (
                <Badge key={subject} variant="secondary" className="text-xs">
                  {subject}
                </Badge>
              ))}
              {(tutor.subjects || []).length > 4 && (
                <Badge variant="outline" className="text-xs">
                  +{tutor.subjects.length - 4} more
                </Badge>
              )}
            </div>
            
            {/* Badges */}
            <div className="flex items-center gap-2 mt-2">
              {(tutor.badges || []).includes('dbs_checked') && (
                <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                  <Shield className="w-3 h-3 mr-1" /> DBS
                </Badge>
              )}
              {(tutor.badges || []).includes('slts_registered') && (
                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                  🇱🇰 SLTS
                </Badge>
              )}
              {(tutor.badges || []).includes('qualified_teacher') && (
                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                  <Award className="w-3 h-3 mr-1" /> Qualified
                </Badge>
              )}
              {(tutor.badges || []).includes('identity_verified') && (
                <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                  ✓ Verified
                </Badge>
              )}
            </div>
            
            {/* Footer: Price & Availability */}
            <div className="flex items-center justify-between mt-3 pt-3 border-t">
              <div className="flex items-center gap-3 text-sm text-gray-600">
                {tutor.availability?.online && (
                  <span className="flex items-center gap-1">
                    <Globe className="w-3.5 h-3.5 text-indigo-500" /> Online
                  </span>
                )}
                {tutor.availability?.in_person && (
                  <span className="flex items-center gap-1">
                    <Users className="w-3.5 h-3.5 text-green-500" /> In-Person
                  </span>
                )}
              </div>
              
              <div className="text-right">
                <span className="text-lg font-bold text-indigo-600">
                  {currencySymbol}{tutor.hourly_rate}
                </span>
                <span className="text-sm text-gray-500">/hr</span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function TutorSearchPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filterOptions, setFilterOptions] = useState({ subjects: [], levels: [], price_range: { min_price: 0, max_price: 100 } });
  const [showFilters, setShowFilters] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    q: searchParams.get('q') || '',
    country: searchParams.get('country') || 'UK',
    subjects: searchParams.get('subjects')?.split(',').filter(Boolean) || [],
    levels: searchParams.get('levels')?.split(',').filter(Boolean) || [],
    region: searchParams.get('region') || '',
    min_price: searchParams.get('min_price') ? parseFloat(searchParams.get('min_price')) : 0,
    max_price: searchParams.get('max_price') ? parseFloat(searchParams.get('max_price')) : 500,
    min_rating: searchParams.get('min_rating') ? parseFloat(searchParams.get('min_rating')) : 0,
    has_dbs: searchParams.get('has_dbs') === 'true',
    online_only: searchParams.get('online_only') === 'true',
    sort_by: searchParams.get('sort_by') || 'rating',
    page: parseInt(searchParams.get('page')) || 1,
  });
  
  const regions = filters.country === 'UK' ? UK_REGIONS : LK_REGIONS;
  
  // Fetch tutors
  const fetchTutors = useCallback(async () => {
    setLoading(true);
    
    try {
      const params = new URLSearchParams();
      
      if (filters.q) params.append('q', filters.q);
      if (filters.country) params.append('country', filters.country);
      if (filters.subjects.length) params.append('subjects', filters.subjects.join(','));
      if (filters.levels.length) params.append('levels', filters.levels.join(','));
      if (filters.min_price > 0) params.append('min_price', filters.min_price.toString());
      if (filters.max_price < 500) params.append('max_price', filters.max_price.toString());
      if (filters.min_rating > 0) params.append('min_rating', filters.min_rating.toString());
      if (filters.has_dbs) params.append('has_dbs', 'true');
      if (filters.online_only) params.append('online_only', 'true');
      params.append('sort_by', filters.sort_by);
      params.append('page', filters.page.toString());
      params.append('limit', '12');
      
      const response = await fetch(`${API_URL}/api/education-tuition/tutors/search?${params}`);
      const data = await response.json();
      
      if (data.success) {
        setTutors(data.tutors || []);
        setTotalResults(data.total || 0);
        setTotalPages(data.total_pages || 0);
        setFilterOptions(data.filters || { subjects: [], levels: [], price_range: {} });
      }
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Failed to search tutors');
    } finally {
      setLoading(false);
    }
  }, [filters]);
  
  useEffect(() => {
    fetchTutors();
  }, [fetchTutors]);
  
  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.q) params.set('q', filters.q);
    if (filters.country !== 'UK') params.set('country', filters.country);
    if (filters.subjects.length) params.set('subjects', filters.subjects.join(','));
    if (filters.levels.length) params.set('levels', filters.levels.join(','));
    if (filters.region) params.set('region', filters.region);
    if (filters.min_price > 0) params.set('min_price', filters.min_price.toString());
    if (filters.max_price < 500) params.set('max_price', filters.max_price.toString());
    if (filters.min_rating > 0) params.set('min_rating', filters.min_rating.toString());
    if (filters.has_dbs) params.set('has_dbs', 'true');
    if (filters.online_only) params.set('online_only', 'true');
    if (filters.sort_by !== 'rating') params.set('sort_by', filters.sort_by);
    if (filters.page > 1) params.set('page', filters.page.toString());
    
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);
  
  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };
  
  const toggleSubject = (subject) => {
    setFilters(prev => {
      const subjects = prev.subjects.includes(subject)
        ? prev.subjects.filter(s => s !== subject)
        : [...prev.subjects, subject];
      return { ...prev, subjects, page: 1 };
    });
  };
  
  const toggleLevel = (level) => {
    setFilters(prev => {
      const levels = prev.levels.includes(level)
        ? prev.levels.filter(l => l !== level)
        : [...prev.levels, level];
      return { ...prev, levels, page: 1 };
    });
  };
  
  const clearFilters = () => {
    setFilters({
      q: '',
      country: filters.country,
      subjects: [],
      levels: [],
      region: '',
      min_price: 0,
      max_price: 500,
      min_rating: 0,
      has_dbs: false,
      online_only: false,
      sort_by: 'rating',
      page: 1,
    });
  };
  
  const activeFilterCount = [
    filters.subjects.length > 0,
    filters.levels.length > 0,
    filters.region,
    filters.min_price > 0,
    filters.max_price < 500,
    filters.min_rating > 0,
    filters.has_dbs,
    filters.online_only,
  ].filter(Boolean).length;
  
  const currencySymbol = filters.country === 'LK' ? 'Rs.' : '£';
  const maxPriceValue = filters.country === 'LK' ? 10000 : 200;
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-bold mb-2">Find a Tutor</h1>
          <p className="text-indigo-100">Search from verified tutors in {filters.country === 'UK' ? 'United Kingdom' : 'Sri Lanka'}</p>
          
          {/* Search Bar */}
          <div className="mt-6 flex gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input 
                value={filters.q}
                onChange={(e) => updateFilter('q', e.target.value)}
                placeholder="Search by subject, name, or topic..."
                className="pl-10 h-12 bg-white text-gray-900"
                data-testid="search-input"
              />
            </div>
            
            <Select value={filters.country} onValueChange={(v) => updateFilter('country', v)}>
              <SelectTrigger className="w-40 h-12 bg-white text-gray-900" data-testid="country-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="UK">🇬🇧 UK</SelectItem>
                <SelectItem value="LK">🇱🇰 Sri Lanka</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              variant={showFilters ? 'default' : 'secondary'}
              className="h-12"
              onClick={() => setShowFilters(!showFilters)}
              data-testid="toggle-filters-btn"
            >
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Filters
              {activeFilterCount > 0 && (
                <Badge className="ml-2 bg-white text-indigo-600">{activeFilterCount}</Badge>
              )}
            </Button>
          </div>
        </div>
      </div>
      
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Filters Sidebar */}
          {showFilters && (
            <div className="w-72 flex-shrink-0">
              <Card className="sticky top-4">
                <CardContent className="p-4 space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Filter className="w-4 h-4" /> Filters
                    </h3>
                    {activeFilterCount > 0 && (
                      <Button variant="ghost" size="sm" onClick={clearFilters}>
                        Clear all
                      </Button>
                    )}
                  </div>
                  
                  {/* Region */}
                  <div>
                    <Label className="text-sm font-medium">Region</Label>
                    <Select value={filters.region || "all"} onValueChange={(v) => updateFilter('region', v === 'all' ? '' : v)}>
                      <SelectTrigger className="mt-1.5" data-testid="region-filter">
                        <SelectValue placeholder="All regions" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All regions</SelectItem>
                        {regions.map(region => (
                          <SelectItem key={region.value} value={region.value}>
                            {region.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Subjects */}
                  <div>
                    <Label className="text-sm font-medium">Subjects</Label>
                    <div className="flex flex-wrap gap-1.5 mt-2 max-h-32 overflow-y-auto">
                      {(filterOptions.subjects || []).slice(0, 15).map(subject => (
                        <Badge 
                          key={subject}
                          variant={filters.subjects.includes(subject) ? 'default' : 'outline'}
                          className="cursor-pointer text-xs"
                          onClick={() => toggleSubject(subject)}
                        >
                          {subject}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  
                  {/* Levels */}
                  <div>
                    <Label className="text-sm font-medium">Levels</Label>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {(filterOptions.levels || []).map(level => (
                        <Badge 
                          key={level}
                          variant={filters.levels.includes(level) ? 'default' : 'outline'}
                          className="cursor-pointer text-xs"
                          onClick={() => toggleLevel(level)}
                        >
                          {level}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  
                  {/* Price Range */}
                  <div>
                    <Label className="text-sm font-medium">
                      Price Range ({currencySymbol}{filters.min_price} - {currencySymbol}{filters.max_price}/hr)
                    </Label>
                    <div className="mt-3 px-2">
                      <Slider
                        value={[filters.min_price, filters.max_price]}
                        min={0}
                        max={maxPriceValue}
                        step={filters.country === 'LK' ? 100 : 5}
                        onValueChange={([min, max]) => {
                          setFilters(prev => ({ ...prev, min_price: min, max_price: max, page: 1 }));
                        }}
                        data-testid="price-slider"
                      />
                    </div>
                  </div>
                  
                  {/* Rating */}
                  <div>
                    <Label className="text-sm font-medium">Minimum Rating</Label>
                    <div className="flex gap-2 mt-2">
                      {[0, 3, 4, 4.5].map(rating => (
                        <Button 
                          key={rating}
                          variant={filters.min_rating === rating ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => updateFilter('min_rating', rating)}
                          className="flex items-center gap-1"
                        >
                          {rating === 0 ? 'Any' : (
                            <>
                              {rating}+
                              <Star className="w-3 h-3 fill-current" />
                            </>
                          )}
                        </Button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Verification */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Verification</Label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox 
                        checked={filters.has_dbs}
                        onCheckedChange={(v) => updateFilter('has_dbs', v)}
                        data-testid="dbs-filter"
                      />
                      <span className="text-sm">
                        {filters.country === 'UK' ? 'DBS Checked' : 'Police Clearance'}
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox 
                        checked={filters.online_only}
                        onCheckedChange={(v) => updateFilter('online_only', v)}
                        data-testid="online-filter"
                      />
                      <span className="text-sm">Online Tutors Only</span>
                    </label>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* Results */}
          <div className="flex-1">
            {/* Results Header */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-gray-600">
                {loading ? 'Searching...' : (
                  <>
                    <span className="font-semibold text-gray-900">{totalResults}</span> tutors found
                  </>
                )}
              </p>
              
              <Select value={filters.sort_by} onValueChange={(v) => updateFilter('sort_by', v)}>
                <SelectTrigger className="w-48" data-testid="sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rating">Highest Rated</SelectItem>
                  <SelectItem value="reviews">Most Reviews</SelectItem>
                  <SelectItem value="price_low">Price: Low to High</SelectItem>
                  <SelectItem value="price_high">Price: High to Low</SelectItem>
                  <SelectItem value="newest">Newest</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Active Filters */}
            {(filters.subjects.length > 0 || filters.levels.length > 0) && (
              <div className="flex flex-wrap gap-2 mb-4">
                {filters.subjects.map(subject => (
                  <Badge key={subject} variant="secondary" className="pl-2 pr-1">
                    {subject}
                    <button 
                      onClick={() => toggleSubject(subject)}
                      className="ml-1 hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
                {filters.levels.map(level => (
                  <Badge key={level} variant="outline" className="pl-2 pr-1">
                    {level}
                    <button 
                      onClick={() => toggleLevel(level)}
                      className="ml-1 hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            
            {/* Tutor Grid */}
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4].map(i => (
                  <Card key={i} className="animate-pulse">
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        <div className="w-20 h-20 bg-gray-200 rounded-full" />
                        <div className="flex-1 space-y-3">
                          <div className="h-5 w-40 bg-gray-200 rounded" />
                          <div className="h-4 w-24 bg-gray-100 rounded" />
                          <div className="flex gap-2">
                            <div className="h-6 w-16 bg-gray-100 rounded" />
                            <div className="h-6 w-16 bg-gray-100 rounded" />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : tutors.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <GraduationCap className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No tutors found</h3>
                  <p className="text-gray-500 mb-4">Try adjusting your filters or search term</p>
                  <Button variant="outline" onClick={clearFilters}>
                    Clear all filters
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {tutors.map(tutor => (
                  <TutorCard 
                    key={tutor.id}
                    tutor={tutor}
                    onClick={() => navigate(`/tutor/${tutor.id}`)}
                  />
                ))}
              </div>
            )}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-8">
                <Button 
                  variant="outline"
                  disabled={filters.page === 1}
                  onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  Previous
                </Button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (filters.page <= 3) {
                      pageNum = i + 1;
                    } else if (filters.page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = filters.page - 2 + i;
                    }
                    
                    return (
                      <Button 
                        key={pageNum}
                        variant={filters.page === pageNum ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setFilters(prev => ({ ...prev, page: pageNum }))}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                <Button 
                  variant="outline"
                  disabled={filters.page === totalPages}
                  onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
